'use strict';

/**
 * ACESSO POR EMPRESA — a regra ÚNICA de quais empresas um usuário acessa e com quais perfis.
 * Usada pelas claims do servidor (`setCustomClaims` → `hasAccessToCompany`, regras do Firestore)
 * e pela tela (`auth.js` → `$companyModulesValidate` e a guarda de rota), para os dois nunca
 * discordarem.
 *
 * Acesso a uma empresa — o critério do `UserEntity.fixComputed` (`companyAndFiliaisAccessIds`):
 * - a entrada dela em `companiesUser` está ativa (`active`) e com `hasAccess`; ou
 * - uma entrada ativa com `hasAccessFiliais` a tem em `filiaisIds` (acesso pela cooperativa).
 * O `hasAuth` e o `active` do usuário são conferidos por quem chama (sem eles, não há claims).
 *
 * Perfis numa empresa com acesso:
 * - os da entrada dela (`profilesIds`), quando está ativa e com `hasAccess`;
 * - os que a cooperativa concede a ela como filial: `filiaisProfiles[empresa]` da entrada ativa da
 *   cooperativa que a tem em `filiaisIds`.
 * Os perfis que o usuário tem NA cooperativa não valem na filial. É a regra da tela; o servidor os
 * somava em cada filial com `hasAccessFiliais` (decisão do FCM, 26/09/2026 — 15 usuários perderam
 * no servidor um perfil de filial que a tela nunca deu).
 *
 * Antes, o servidor criava chave de claim para TODA entrada de `companiesUser` (inclusive inativa
 * ou sem acesso) e a tela lia `filiaisProfiles` pela chave da cooperativa, e não pela da filial.
 * Ver docs/financial/Recebedores.md (G8, G9).
 */

exports.default = exports;

function union(a, b) {
  const out = (a || []).slice();
  for (const x of b || []) {
    if (!out.includes(x)) {
      out.push(x);
    }
  }
  return out;
}

/**
 * @param {?{companiesUser?: Object<string, object>}} user documento do usuário
 * @returns {Object<string, string[]>} empresa → perfis, só para as empresas com acesso
 */
function companyAccessProfiles(user) {
  const entries = user?.companiesUser || {};
  const out = {};
  for (const [companyId, entry] of Object.entries(entries)) {
    if (!entry?.active) {
      continue;
    }
    if (entry.hasAccess) {
      out[companyId] = union(out[companyId], entry.profilesIds);
    }
    if (entry.hasAccessFiliais) {
      for (const filialId of entry.filiaisIds || []) {
        out[filialId] = union(out[filialId], []);
      }
    }
  }
  for (const entry of Object.values(entries)) {
    if (!entry?.active) {
      continue;
    }
    for (const filialId of entry.filiaisIds || []) {
      if (filialId in out) {
        out[filialId] = union(out[filialId], entry.filiaisProfiles?.[filialId]);
      }
    }
  }
  return out;
}
exports.companyAccessProfiles = companyAccessProfiles;
